// hook which trigger the oauth flow for a given app and auth provider
import { getDomainConfig } from '@/config/domainConfig';

const BASE_URL = getDomainConfig().apiBaseUrl;

// Utility function to open OAuth window. Returns the popup handle (or null if
// the browser blocked it) so callers can detect a cancelled/closed popup.
export const openOAuthWindow = (app_name: string, auth_name: string = "default"): Window | null => {
  const url = `${BASE_URL}/auth/oauth/${app_name}/${auth_name}?token_dispatch_mode=browser`;
  return window.open(url, "", "width=800,height=600");
};

/**
 * Opens the OAuth popup for an authorization performed on behalf of an
 * existing connection.
 *
 * `credential_id` names the connection whose own OAuth client the handshake
 * runs against. The authorize route is authenticated and this popup is a
 * same-origin top-level navigation, so the session travels with it and the
 * server resolves the connection against this tenant. Without an id the route
 * falls back to the app's platform-owned client — what every other app does.
 */
export const openOAuthWindowForConnection = (
  app_name: string,
  auth_name: string,
  credentialId: string,
): Window | null => {
  const url = `${BASE_URL}/auth/oauth/${app_name}/${auth_name}`
    + `?credential_id=${encodeURIComponent(credentialId)}`;
  return window.open(url, "", "width=800,height=600");
};

const useConnectToOauthApps = (app_name: string, auth_name: string) => {
  return () => openOAuthWindow(app_name, auth_name);
};

export default useConnectToOauthApps;
/** How often to check whether the authorization popup has closed. */
const POPUP_POLL_INTERVAL_MS = 500;

/** Stop watching a popup the user left open, so the timer cannot leak. */
const POPUP_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Runs a callback once the authorization popup closes.
 *
 * A handshake opened against a connection completes on the server, so the
 * popup carries nothing back to react to — polling `closed` is the only signal
 * the browser gets. Does nothing when the popup was blocked, and gives up once
 * the window has been open long enough that the user has plainly abandoned it.
 */
export const whenPopupCloses = (popup: Window | null, onClosed: () => void): void => {
  if (!popup) return;

  const startedAt = Date.now();
  const timer = setInterval(() => {
    const expired = Date.now() - startedAt > POPUP_POLL_TIMEOUT_MS;

    if (popup.closed || expired) {
      clearInterval(timer);
      if (!expired) onClosed();
    }
  }, POPUP_POLL_INTERVAL_MS);
};
